import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CircleNotch,
  Warning,
  Receipt,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createCreditNote, type CreditNote } from '@/lib/creditNotesApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

function fmtMoney(value: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

interface Props {
  /** The fee note being credited */
  feeNoteId: string;
  feeNoteNumber: string;
  /** Gross total on the original fee note */
  feeNoteTotal: string;
  /** How much has already been paid against this fee note */
  amountPaid: string;
  /** How much has already been credited against this fee note */
  creditedAmount: string;
  /**
   * Server-authoritative outstanding balance (refund/write-off aware). When supplied this
   * is used as the credit cap instead of recomputing — the canonical fact is owned by the
   * server (computeFeeNoteOutstanding). The local compute below is only a fallback.
   */
  outstanding?: number;
  currency?: string;
  onClose: () => void;
  onCreated: (cn: CreditNote) => void;
}

export function CreditNoteModal({
  feeNoteId,
  feeNoteNumber,
  feeNoteTotal,
  amountPaid,
  creditedAmount,
  outstanding,
  currency = 'GBP',
  onClose,
  onCreated,
}: Props) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const total = parseFloat(feeNoteTotal) || 0;
  const paid = parseFloat(amountPaid) || 0;
  const credited = parseFloat(creditedAmount) || 0;
  // MONEY-2: prefer the server-authoritative outstanding (refund/write-off aware). The local
  // total-paid-credited compute is only a fallback for callers that don't pass it.
  const effectiveOutstanding = outstanding ?? Math.max(0, total - paid - credited);

  const enteredAmount = parseFloat(amount) || 0;
  const amountError =
    enteredAmount > 0 && enteredAmount > effectiveOutstanding + 0.005
      ? `Cannot exceed the outstanding balance of ${fmtMoney(effectiveOutstanding, currency)}`
      : null;
  const reasonError = reason.length > 0 && reason.length < 10
    ? 'Reason must be at least 10 characters'
    : null;

  const canSubmit =
    enteredAmount > 0 &&
    enteredAmount <= effectiveOutstanding + 0.005 &&
    reason.trim().length >= 10 &&
    !amountError;

  const createMut = useMutation({
    mutationFn: () =>
      createCreditNote({
        originalFeeNoteId: feeNoteId,
        totalAmount: enteredAmount,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (cn) => {
      toast.success(`Credit note ${cn.creditNoteNumber} issued`);
      qc.invalidateQueries({ queryKey: ['credit-notes'] });
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary'] });
      onCreated(cn);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not issue credit note')),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !createMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex w-full max-w-md flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <ArrowCounterClockwise size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Issue credit note
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Against fee note {feeNoteNumber}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !createMut.isPending && onClose()}
            disabled={createMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {/* Fee note summary */}
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                  Invoice total
                </div>
                <div className="font-semibold">{fmtMoney(total, currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                  Paid
                </div>
                <div className="font-semibold text-emerald-700 dark:text-emerald-400">{fmtMoney(paid, currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                  Outstanding
                </div>
                <div className="font-semibold text-foreground">
                  {fmtMoney(effectiveOutstanding, currency)}
                </div>
              </div>
            </div>
          </div>

          {effectiveOutstanding <= 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2.5">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                This invoice has no outstanding balance to credit.
              </p>
            </div>
          )}

          {/* Credit amount */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Credit amount (£)
            </label>
            <div className="relative">
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                max={effectiveOutstanding.toFixed(2)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={createMut.isPending || effectiveOutstanding <= 0}
              />
              {effectiveOutstanding > 0 && !amount && (
                <button
                  type="button"
                  onClick={() => setAmount(effectiveOutstanding.toFixed(2))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-brand-600 dark:text-brand-400 hover:underline"
                >
                  max {fmtMoney(effectiveOutstanding, currency)}
                </button>
              )}
            </div>
            {amountError && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <Warning size={11} weight="fill" />
                {amountError}
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Reason for credit
              <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground/70">
                (min 10 characters — appears on the credit note document)
              </span>
            </label>
            <ExpandableTextarea
              label="Reason for credit"
              value={reason}
              onChange={setReason}
              rows={3}
              maxLength={2000}
              placeholder="e.g. Agreed reduction following hearing adjournment on 3 June 2026"
              disabled={createMut.isPending}
            />
            {reasonError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{reasonError}</p>
            )}
            <p className="mt-1 text-right text-[10px] text-muted-foreground/60">
              {reason.length}/2000
            </p>
          </div>

          {/* Internal notes */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Internal notes{' '}
              <span className="font-normal normal-case text-muted-foreground/70">
                (optional — not on the credit note document)
              </span>
            </label>
            <ExpandableTextarea
              label="Internal notes"
              value={notes}
              onChange={setNotes}
              rows={2}
              maxLength={2000}
              placeholder="Context for the chambers file"
              disabled={createMut.isPending}
            />
          </div>

          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/20 px-3 py-2.5">
            <Receipt size={13} weight="duotone" className="mt-0.5 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              A credit note reduces the outstanding balance on the invoice. If the credit
              fully settles the balance, the invoice status will update to{' '}
              <span className="font-medium">Credited</span>.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={createMut.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={createMut.isPending || !canSubmit}
            onClick={() => createMut.mutate()}
            className="gap-1.5"
          >
            {createMut.isPending ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <ArrowCounterClockwise size={14} weight="fill" />
            )}
            Issue credit note
          </Button>
        </div>
      </Card>
    </div>
  );
}
